/**
 * One product in the CenoX ecosystem.
 *
 * Everything shown here comes from the server: the name, the description, the
 * state, and which single action to offer. The card chooses no rules — it
 * renders the answer.
 *
 * The one thing it does decide is what a press *does*, and that follows the
 * action's kind rather than the product: `navigate` goes to the product,
 * `activate` asks the server to initialize it, `none` is not a button anybody
 * can press. Rendering this card never provisions anything.
 */

import { useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AlertCircle, ArrowRight, Check, Loader2, Lock } from 'lucide-react';
import { ApiError, api } from '../../lib/api';
import { useAuth, useBusinessPath } from '../../lib/auth';
import {
  PRODUCT_ACTIONS,
  productHref,
  statusLine,
  type PlatformProduct,
} from '../../lib/platformProducts';
import { Badge, Button, cn } from '../ui';

/** A quiet colour per product, so the grid is scannable without being loud. */
const TINT: Record<string, string> = {
  ai: 'from-violet-500 to-fuchsia-500',
  business: 'from-blue-500 to-indigo-500',
  verify: 'from-blue-600 to-cyan-500',
  growth: 'from-emerald-500 to-teal-500',
  automation: 'from-sky-500 to-cyan-500',
  train: 'from-amber-500 to-orange-500',
  robotics: 'from-rose-500 to-pink-500',
  autonomous: 'from-indigo-500 to-violet-500',
  intelligence: 'from-slate-600 to-slate-800',
};

function StateBadge({ product }: { product: PlatformProduct }): ReactNode {
  const s = product.provisioningStatus;
  if (s === 'failed') return <Badge tone="danger">Setup failed</Badge>;
  if (s === 'pending' || s === 'provisioning') return <Badge tone="info">Setting up</Badge>;
  if (product.entitled && product.activated && !product.enabled)
    return <Badge tone="warning">Switched off</Badge>;
  if (product.entitled && product.activated) return <Badge tone="success">Active</Badge>;
  if (product.entitled) return <Badge tone="info">Included</Badge>;
  if (product.catalogueStatus !== 'available') return <Badge tone="neutral">Coming soon</Badge>;
  return <Badge tone="neutral">Locked</Badge>;
}

export function ProductCard({ product }: { product: PlatformProduct }): ReactNode {
  const navigate = useNavigate();
  const { business } = useAuth();
  const hasWorkspace = Boolean(business);
  const path = useBusinessPath();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  const action = PRODUCT_ACTIONS[product.availableAction];

  const activate = useMutation({
    mutationFn: () => api.post(path(`/platform/products/${product.product}/activate`), {}),
    onSuccess: () => {
      setError(null);
      void queryClient.invalidateQueries({ queryKey: ['platform-products'] });
    },
    onError: (caught) =>
      setError(
        caught instanceof ApiError ? caught.message : 'Could not start this product’s setup.',
      ),
  });

  const press = (): void => {
    if (action.kind === 'navigate') navigate(productHref(product));
    if (action.kind === 'activate') activate.mutate();
  };

  const locked = !product.entitled;

  return (
    <div
      className={cn(
        'flex flex-col rounded-2xl border bg-white p-5 transition-shadow',
        locked ? 'border-slate-200' : 'border-slate-200 hover:shadow-md',
      )}
    >
      <div className="flex items-start gap-3">
        <div
          className={cn(
            'flex size-10 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br text-white',
            TINT[product.product] ?? 'from-slate-400 to-slate-600',
            locked && 'opacity-45',
          )}
        >
          {locked ? (
            <Lock className="size-4" aria-hidden />
          ) : product.activated ? (
            <Check className="size-5" aria-hidden />
          ) : (
            <ArrowRight className="size-5" aria-hidden />
          )}
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="truncate text-sm font-semibold text-slate-900">{product.name}</h3>
            <StateBadge product={product} />
          </div>
          {product.description && (
            <p className="mt-0.5 line-clamp-2 text-sm text-slate-500">{product.description}</p>
          )}
        </div>
      </div>

      <p
        className={cn(
          'mt-4 flex items-start gap-1.5 text-xs',
          product.provisioningStatus === 'failed' ? 'text-red-700' : 'text-slate-500',
        )}
      >
        {product.provisioningStatus === 'failed' && (
          <AlertCircle className="mt-px size-3.5 shrink-0" aria-hidden />
        )}
        {product.provisioningStatus === 'pending' || product.provisioningStatus === 'provisioning' ? (
          <Loader2 className="mt-px size-3.5 shrink-0 animate-spin" aria-hidden />
        ) : null}
        {statusLine(product, hasWorkspace)}
      </p>

      {error && (
        <p role="alert" className="mt-2 text-xs text-red-700">
          {error}
        </p>
      )}

      <div className="mt-4 flex items-center justify-between gap-3 pt-1">
        {/*
          With no workspace there is nothing for this button to buy or open;
          the page offers Create Business / Join instead.
        */}
        {(hasWorkspace || product.catalogueStatus !== 'available') && (
        <Button
          size="sm"
          variant={action.variant}
          onClick={press}
          loading={activate.isPending}
          /*
            `none` is a state, not a button. "Provisioning" and "Coming soon"
            describe what is true; offering them as something to press would be
            offering an action that cannot happen.
          */
          disabled={action.kind === 'none'}
        >
          {action.label}
        </Button>
        )}

        {/*
          The route, shown plainly. A person looking at eight cards should be
          able to tell where each one lives without pressing anything.
        */}
        <span className="font-mono text-xs text-slate-400">{productHref(product)}</span>
      </div>
    </div>
  );
}
